import { amplifyClient, getEndpointV2 } from 'api';
import { getUrlToFetchOne as getUrlToFetchOneCompany } from 'features/companies/endpoints';
import { createApiSlice } from 'helpers/createApiSlice';
import { call, select } from 'redux-saga/effects';
import { authSignIn } from 'store/features/authSignIn';
import { baseConfig } from 'store/features/baseConfig';
import {
  ApiStateData,
  AuthData,
  Company,
  MakerSelectors,
  UserData,
  UserDto,
  UserProfile,
  UserRoleBase,
  UserRoleRestriction,
  UserType,
} from 'types';

type Data = UserData;

interface ExtraSelectors extends MakerSelectors<ApiStateData<Data>> {
  getUserEmail: (d: ApiStateData<Data>) => string | undefined;
  getUserName: (d: ApiStateData<Data>) => string | undefined;
  getUserCompanyId: (d: ApiStateData<Data>) => string | undefined;
  getUserProfile: (d: ApiStateData<Data>) => UserProfile | undefined;
  getUserType: (d: ApiStateData<Data>) => UserType | undefined;
  getUserId: (d: ApiStateData<Data>) => string;
  getRoles: (d: ApiStateData<Data>) => Array<string>;
  getRoleRestrictions: (d: ApiStateData<Data>) => (role: UserRoleBase) => Record<
    UserRoleRestriction | 'some',
    boolean
  > & {
    someOf: (rests: Array<UserRoleRestriction>) => boolean;
    everyOf: (rests: Array<UserRoleRestriction>) => boolean;
  };
}

export const userFetch = createApiSlice<Data, undefined, ExtraSelectors>({
  //
  name: 'userFetch',
  *tryGenerator() {
    const authData: AuthData = yield select(authSignIn.selectors.getData);
    const isSupport: boolean = yield select(authSignIn.selectors.getIsSupport);

    const { data } = yield call(amplifyClient, {
      method: 'get',
      url: getEndpointV2({
        path: '/auth/user/:username',
        urlParams: { username: authData.username },
      }),
    });

    const {
      creationDate,
      company: companyId,
      username,
      name,
      status,
      userEmail,
      userPhone,
      profileId,
      profileRoles,
      id,
      metadata,
    } = data as UserDto;

    const userType = isSupport ? 'ADMIN' : 'USER';

    /**
     * Data out
     */
    const userData: UserData = {
      companyId,
      creationDate,
      username,
      name,
      status,
      userEmail,
      userType,
      profileRoles,
      profileId,
      userPhone,
      metadata,
      id,
    };

    if (userType !== 'ADMIN') {
      /**
       * Fecthing profiles
       */
      const { data: profiles }: { data: Array<UserProfile> } = yield call(amplifyClient, {
        method: 'get',
        url: getEndpointV2({ path: '/auth/company/:companyId/profile', urlParams: { companyId } }),
      });

      userData.userProfile = profiles.find((profile: UserProfile) => profile.id === profileId);

      /**
       * Fecthing companyProfile
       */
      const { data }: { data: Company } = yield call(amplifyClient, {
        method: 'get',
        url: getUrlToFetchOneCompany(companyId),
      });

      userData.companyProfile = data.metadata.companyProfile;
    }

    return userData;
  },
  extraSelectors: {
    getUserEmail: (data) => data?.userEmail,
    getUserName: (data) => data?.name,
    getUserCompanyId: (data) => data?.companyId,
    getUserType: (data) => data?.userType,
    getUserProfile: (data) => data?.userProfile,
    getUserId: (data) => data?.username || '<unknown userId>',
    getRoles: (data) => data?.profileRoles || [],
    getRoleRestrictions: (data) => (baseRole) => {
      // const roleRestrictions = (data?.profileRoles
      //   .filter((role) => role.split(':')[0] === baseRole)
      //   ?.map((role) => role.split(':')[1]) || []) as Array<UserRoleRestriction>;

      const getRestrictionValue = (rest: UserRoleRestriction) => {
        /**
         * TODO enable when the backend be ready
         */
        // return roleRestrictions.includes(rest);

        return true;
      };

      const restrictions: Record<UserRoleRestriction, boolean> = {
        add: getRestrictionValue('add'),
        delete: getRestrictionValue('delete'),
        full: getRestrictionValue('full'),
        list: getRestrictionValue('list'),
        read: getRestrictionValue('read'),
        update: getRestrictionValue('update'),
      };

      return {
        ...restrictions,
        some: Object.values(restrictions).some(Boolean), // has some restriction
        someOf: (rests) => rests.some(getRestrictionValue), // has some restriction of the list
        everyOf: (rests) => rests.every(getRestrictionValue), // has every restrictions of the list
      };
    },
  },
});
